/**
 * This module serves as "interceptor" or "middleware" before key workflows reports
 */
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import type {Location} from 'react-router';

import {MetricType} from 'common/query-builder/query-block/constants';

import {ReportName} from '../constants';
import {InsightsBookmarkParams} from '../insights/util/types';
import {useReportConsistency, useSingleReport} from '../util/feature-sampling';
import type {BookmarkURLParams} from '../queries/app-api/bookmark-urls-client';
import {getBehaviorShowClauses, isFormulaShowClause} from '../insights/models/utils';

// Reduce segmentation limits for insights line charts for certain projects
const REDUCED_LINE_LIMIT_PROJECT_IDS = [`2718323`, `2717081`, `2899503`, `2880915`, `2928584`];

// Reduced segmentation limits for all insights charts for certain projects
const REDUCED_LIMIT_PROJECT_IDS = [`2718323`, `2928584`];

/**
 * Runs before making query to query-api
 *
 * It could dynamically transform current query payload and add special handlings if needed
 *
 * For now our serialized version of bookmarkParams is still in old format
 * So we do transformation from old -> new
 *
 * Once we convert the bookmarkParams shape to new format
 * we have to do transformation from new -> old depending on feature flag
 */
export function beforeQuery({
  endpoint,
  urlParams,
  body,
  location,
}: {
  // insights / funnels / flows / retention
  endpoint: string;
  // includes project_id etc.
  urlParams: URLSearchParams;
  // request payload for qapi
  body: TODO;
  location: Location;
}) {
  let transformedBody = body;
  // Temporary fix for CrunchyRoll + OneFootball
  // TODO jayantj: remove once merger optimization is shipped
  transformedBody = addLimitsToSlowProjects({urlParams, endpoint, body: transformedBody});

  transformedBody = addForecastToDisplayOptions({endpoint, body: transformedBody, location});
  transformedBody = transformInsightsSingleReportPayload({endpoint, body: transformedBody, location});

  return {
    endpoint,
    urlParams,
    body: transformedBody,
  };
}

/**
 * Hook for dynamically transform cleaner url params
 *
 * For now, we just remove the extra field `behaviorType` on show clauses
 */
export function beforeCleanerUrl(params: BookmarkURLParams) {
  const result = omitBy(omit(params, [`project_id`]), (value) => isNil(value));
  const {type, params: bookmarkParams} = params;
  if (type === ReportName.Insights && !useSingleReport() && !useReportConsistency()) {
    // transform show clause
    return {
      ...result,
      params: transformToOriginalBookmarkParams(bookmarkParams as InsightsBookmarkParams),
    };
  }
  return result;
}

/**
 * Hook to run before saving bookmarks to the webapp
 */
export function beforeSavingBookmark({params, type}: {params: unknown; type: string}) {
  if (type === ReportName.Insights && !useSingleReport() && !useReportConsistency()) {
    return transformToOriginalBookmarkParams(params as InsightsBookmarkParams);
  }
  return params;
}

//==================================================================================================
// The rest are private pure functions to help with the above 2 hooks
// can spread out those functions in different files if needed
//==================================================================================================
function transformFunnels({bookmarkParams}: {bookmarkParams: InsightsBookmarkParams}) {
  return {
    ...bookmarkParams,
    displayOptions: {
      ...bookmarkParams.displayOptions,
      funnelStepsSelectedTableColumns: {
        'conv-first-step': false,
        'conv-prev-step': true,
        count: true,
        'stat-sig': true,
        'time-first-step': false,
        'time-prev-step': true,
      },
    },
  };
}

// TODO make this function to work when there are different types of behavior metrics in the show section
function transformBookmarkParams({
  bookmarkParams,
  location,
}: {
  bookmarkParams: InsightsBookmarkParams;
  location: Location;
}) {
  if (!useSingleReport(location) && !useReportConsistency()) {
    return transformToOriginalBookmarkParams(bookmarkParams);
  }
  const showSections = bookmarkParams.sections.show;
  if (getBehaviorShowClauses(showSections).some((showClause) => showClause.behavior.type === MetricType.Funnel)) {
    return transformFunnels({bookmarkParams});
  }

  return bookmarkParams;
}

function transformToOriginalBookmarkParams(bookmarkParams: InsightsBookmarkParams) {
  const {cohorts, group} = moveGroupByCohortsToCohortSection(bookmarkParams);
  const show = mapBehaviorMetricToOriginalShowClause(bookmarkParams);

  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      cohorts,
      group,
      show,
    },
  };
}

function moveGroupByCohortsToCohortSection(bookmarkParams: InsightsBookmarkParams) {
  let cohortsSection = [];
  const groupSection = [];

  const groups = bookmarkParams.sections.group || [];
  groups.forEach((group) => {
    const groupByCohorts = group.cohorts || [];
    if (groupByCohorts.length > 0) {
      cohortsSection = groupByCohorts;
    } else {
      groupSection.push(group);
    }
  });

  return {
    cohorts: cohortsSection,
    group: groupSection,
  };
}

function mapBehaviorMetricToOriginalShowClause(bookmarkParams: InsightsBookmarkParams) {
  return bookmarkParams.sections.show.map((show) => {
    if (isFormulaShowClause(show)) {
      return show;
    }

    const {behavior, measurement} = show;
    const {math, perUserAggregation, property, rolling, cumulative, multiAttribution} = measurement;
    const {
      type: _type,
      search,
      resourceType,
      profileType,
      value,
      filters,
      filterDeterminer,
      dataGroupId,
      dataset,
      datasetId,
      projectId,
      customBucket,
      ...restBehavior
    } = behavior;
    return {
      ...restBehavior,
      math,
      perUserAggregation,
      property,
      rolling,
      cumulative,
      multiAttribution,
      search,
      resourceType,
      profileType,
      value,
      filters,
      filterDeterminer,
      dataGroupId,
      dataset,
      datasetId,
      projectId,
      customBucket,
    };
  });
}

function transformInsightsSingleReportPayload({
  endpoint,
  body,
  location,
}: {
  endpoint: string;
  body: any;
  location: Location;
}): InsightsBookmarkParams {
  if (endpoint !== `insights` && endpoint !== `insights/query_metadata`) return body;
  const bookmarkParams = body.bookmark as InsightsBookmarkParams;

  return {
    ...body,
    bookmark: transformBookmarkParams({bookmarkParams, location}),
  };
}

// implements a query param flag for forcing forecast queries, intended only for debugging purposes
function addForecastToDisplayOptions({endpoint, body, location}: {endpoint: string; body: TODO; location: Location}) {
  if (endpoint !== `insights`) return body;
  const bookmarkParams = body.bookmark as InsightsBookmarkParams;
  const searchParams = new URLSearchParams(location.search ?? ``);
  if (
    [`1`, `true`].includes(searchParams.get(`force_forecast`)) &&
    bookmarkParams.displayOptions?.chartType === `line`
  ) {
    return {
      ...body,
      queryLimits: {limit: parseFloat(searchParams.get(`query_limit`) || `20`)},
      bookmark: {
        ...bookmarkParams,
        forecastComparison: {
          trainingMode: searchParams.get(`training`) || `agile`,
          confidence: parseFloat(searchParams.get(`confidence`) || `0.95`),
        },
      },
    };
  }
  return body;
}

function addLimitsToSlowProjects({
  urlParams,
  endpoint,
  body,
}: {
  urlParams: URLSearchParams;
  endpoint: string;
  body: Obj<any>;
}) {
  if (endpoint !== `insights`) return body;
  if (!body.queryLimits) return body;
  const bookmarkParams = body.bookmark as InsightsBookmarkParams;
  let limit = body.queryLimits?.limit;
  if (
    REDUCED_LINE_LIMIT_PROJECT_IDS.includes(urlParams.get(`project_id`)) &&
    bookmarkParams.displayOptions?.chartType === `line`
  ) {
    limit = 100;
  } else if (REDUCED_LIMIT_PROJECT_IDS.includes(urlParams.get(`project_id`)) && body?.format !== `csv`) {
    limit = 300;
  }
  return {
    ...body,
    queryLimits: {
      ...body.queryLimits,
      limit,
    },
  };
}
